import { Color3, Mesh, PhysicsShapeType, Vector3 } from '@babylonjs/core';
import { AssetBasedConfig, DistributionConfig, SpawnPoint } from './AssetObject';
import { createObstacleBehavior } from './behavior/ObstacleBehaviorFactory';
import { ObstacleLaserGridBehavior } from './behavior/ObstacleLaserGridBehavior';
import { ObstacleSpikeRingBehavior } from './behavior/ObstacleSpikeRingBehavior';
import { GameConfig } from './GameConfig';
import { EntityGenerator, Level, PathInfo } from './Level';
import { EntitySoundConfig } from './LevelConfig';
import { AssetObjectConfig, MeshFactory } from './MeshFactory';
import { Obstacle } from './Obstacle';
import { PathFactory } from './PathFactory';

/**
 * Configuration for obstacle spawning in a level
 */
export interface ObstacleConfig extends AssetBasedConfig<ObstacleDistributionConfig> {}

export type ObstacleBehaviorType =
  | 'idle'
  | 'patrol' // Circle around center
  | 'rotate' // Rotate self
  | 'jump' // Periodic hop
  | 'horizontal' // Horizontal move
  | 'vertical' // Vertical move
  | 'scale' // Scaling
  | 'pulse' // Pulsing
  | 'rocket' // Rocket towards player
  | 'roll' // Roll back and forth
  | 'fire' // Periodic fire column trap
  | 'swing' // Pendulum swing trap
  | 'cannon' // Tracking cannon
  | 'hammer' // Hopping hammer smash
  | 'seekerBeam' // Tracking laser beam
  | 'geyser' // Periodic upward blast
  | 'bouncePad' // Launches player upward
  | 'magnet' // Alternating attract/repel
  | 'spikeRing' // Rotating ring of spikes
  | 'laserGrid' // Blinking grid of laser rows
  | 'turret' // Burst-fire turret with lead targeting
  | 'windZone'; // Area that pushes player laterally

export interface ObstacleDistributionConfig extends DistributionConfig {
  /**
   * Behavior type
   */
  behavior: ObstacleBehaviorType;

  /**
   * Lateral offset range
   */
  offsetRange?: { min: number; max: number };

  /**
   * Y offset
   */
  yOffset?: number;

  /**
   * Use level Y position
   */
  useLevelYPosition?: boolean;

  /**
   * Scale range
   */
  scale?: { min: number; max: number };

  /**
   * Speed multiplier for behaviors
   */
  speed?: number;

  /**
   * Rotation to adjust mesh orientation
   */
  rotation?: Vector3;

  /**
   * Align the meshs rotation to the path
   */
  alignToPath?: boolean;

  /**
   * Highlight the obstacle
   */
  highlight?: boolean;

  /**
   * Bake rotation into mesh
   */
  bakeRotation?: Vector3;

  /**
   * Behavior specific params
   */
  radius?: number; // For patrol/circle
  height?: number; // For jump/vertical
  width?: number; // For horizontal
  scaleRange?: { min: number; max: number }; // For scale/pulse, ...
  destructible?: boolean; // If true, can be destroyed
  harmless?: boolean; // If true, player can walk safely collide with it
  physics?: boolean; // If true, has physics
  angle?: number; // angle in radians
  axis?: 'x' | 'y' | 'z'; // Axis for animations/effects eg. to swing around in swing behavior
  direction?: 1 | -1; // Direction multiplier (1 = positive axis, -1 = negative axis)
  sound?: EntitySoundConfig; // may have a sound
  fireInterval?: number; // Time between fire activations (default 2s)
  fireActiveTime?: number; // How long fire stays active (default 1s)
  fireHeight?: number; // Height/length of fire column
  fireWidth?: number; // Width/spread of fire beam and collision (default 1)
  fireEmitterGrow?: boolean; // If true, fire emitter will grow
  // SeekerBeam
  beamRange?: number; // Max tracking/damage range (default 30)
  // Geyser
  geyserInterval?: number; // Seconds between eruptions (default 4)
  geyserActiveTime?: number; // Blast duration (default 1.5)
  geyserForce?: number; // Upward launch force (default 20/25)
  // Magnet
  magnetRadius?: number; // Attraction/repulsion radius (default 12)
  magnetForce?: number; // Force strength (default 8)
  // SpikeRing
  ringRadius?: number; // Spike ring radius (default 3)
  // LaserGrid
  laserRows?: number; // Number of laser rows (default 3)
  // Turret
  turretRange?: number; // Activation range (default 45)
  // WindZone
  windForce?: number; // Lateral push force (default 6)
  windRadius?: number; // Effect radius (default 8)
}

export class ObstacleGenerator implements EntityGenerator<ObstacleConfig> {
  static highlightColor: Color3 = Color3.Red();

  private spawnPoints: SpawnPoint<ObstacleDistributionConfig>[] = [];
  private activeObstacles: Map<string, Obstacle> = new Map();

  private spawnRange: number;
  private despawnRange: number;

  constructor(public level: Level) {}

  public getObstacles(): Obstacle[] {
    return Array.from(this.activeObstacles.values());
  }

  async generate(config: ObstacleConfig): Promise<void> {
    this.spawnRange = config.spawnRange;
    this.despawnRange = config.despawnRange;

    let spawnPointId = 0;

    for (const distribution of config.distributions) {
      const matchingAssets = config.assetObjects.filter((ao) => distribution.types.includes(ao.type));

      if (matchingAssets.length === 0) {
        console.warn(`[ObstacleGenerator] No asset objects found for types: ${distribution.types}`);
        continue;
      }

      const interval = distribution.interval ?? 1;
      const minPoint = distribution.minPoint ?? 0;
      const maxPoint = distribution.maxPoint ?? this.level.pathPoints.length - 1;

      for (let i = 0; i < this.level.pathPoints.length; i += interval) {
        if (i < minPoint || i > maxPoint) {
          continue;
        }

        if (this.level.occupiedPathIndexes.includes(i)) {
          continue;
        }

        if (this.level.config.platform && !this.level.config.way) {
          const platform = this.level.getPlatformByPathIndex(i);
          if (!platform) {
            // no instance at this index because it would be placed in the air
            continue;
          }
          if (platform.config.moving || platform.config.crumbling) {
            // no instance on moving platforms, would require parenting
            continue;
          }
        }

        const probability = this.level.rng.uFloat32();
        const distributionProbability = distribution.probability + (distribution.progressionProbability ?? 0) * i;
        if (distributionProbability && probability > distributionProbability) {
          continue;
        }
        if (distribution.skipProbability && probability < distribution.skipProbability) {
          continue;
        }

        const pathInfo = this.level.getPathInfo(i);
        let position = this.level.getMaxPathPosition(i);

        let lateralOffset = 0;
        if (distribution.offsetRange) {
          lateralOffset = this.level.floatInRange(distribution.offsetRange.min, distribution.offsetRange.max);
          position = position.subtract(pathInfo.lateral.scale(lateralOffset));
        }

        if (distribution.lanes) {
          const lane = this.level.randomArrayElement(distribution.lanes);
          position = position.subtract(pathInfo.lateral.scale(lane * this.level.config.path.laneWidth));
        }

        if (distribution.useLevelYPosition) {
          // Get height at position
          const groundY = this.level.getHeightAtCoordinates(position.x, position.z);
          position.y = groundY;
        }

        if (distribution.yOffset !== undefined) {
          position.y += distribution.yOffset;
        }

        const spawnPositions: Vector3[] = distribution.pattern
          ? PathFactory.getPatternPositions(
              distribution.pattern,
              position,
              this.level.config.path.laneWidth,
              pathInfo,
              distribution.alignToPath
            )
          : [position];

        for (const spawnPosition of spawnPositions) {
          const assetObject = this.level.randomArrayElement(matchingAssets);
          this.spawnPoints.push({
            id: `obstacle_${spawnPointId++}`,
            position: spawnPosition,
            pathIndex: i,
            distribution,
            assetObject,
            spawned: false,
            lateralOffset,
          });
        }
        this.level.occupiedPathIndexes.push(i);
      }
    }
    if (GameConfig.logDebug) {
      console.log(`[ObstacleGenerator] Created ${this.spawnPoints.length} spawn points`);
    }
  }

  update(deltaTime: number): void {
    if (!this.level.player) return;

    for (const spawnPoint of this.spawnPoints) {
      if (spawnPoint.spawned) continue;

      const distance = this.level.getCameraDistance(spawnPoint.position);
      if (distance <= this.spawnRange) {
        this.spawnObstacle(spawnPoint);
      }
    }

    for (const [id, obstacle] of this.activeObstacles) {
      const distance = this.level.getCameraDistance(obstacle.spawnPoint);
      if (distance > this.despawnRange) {
        this.despawnObstacle(id);
      } else {
        obstacle.update(deltaTime);
      }
    }
  }

  private spawnObstacle(spawnPoint: SpawnPoint<ObstacleDistributionConfig>): void {
    const baseScale = spawnPoint.assetObject.scale || 1;
    let scale = baseScale;
    if (spawnPoint.distribution.scale) {
      const randomScale = this.level.floatInRange(spawnPoint.distribution.scale.min, spawnPoint.distribution.scale.max);
      scale = baseScale * randomScale;
    }

    // Clone asset object
    spawnPoint.assetObject.clone = true;

    // Physics configuration
    const physicsType = spawnPoint.distribution.physics ? PhysicsShapeType.MESH : undefined;
    const pathInfo = this.level.getPathInfo(spawnPoint.pathIndex);

    const config = AssetObjectConfig.builder()
      .object(spawnPoint.assetObject)
      .rotation(this.getObstacleRotation(spawnPoint.distribution, pathInfo))
      .physicsShapeType(physicsType)
      .highlight(spawnPoint.distribution.highlight ? ObstacleGenerator.highlightColor : undefined)
      .scale(scale)
      .bakeRotation(spawnPoint.distribution.bakeRotation)
      .build();

    const clonedMesh = config.buildMesh(this.level);
    MeshFactory.configureMesh(clonedMesh, config, this.level);

    // Apply manual position
    clonedMesh.position.copyFrom(spawnPoint.position);
    // If behavior needs to rotate, it will handle it via startRotation
    // We can also apply random rotation if config supported it (not explicitly requested yet but standard)

    // Register collision meshes with player
    const childMeshes = clonedMesh.getChildMeshes(false, (n) => n instanceof Mesh) as Mesh[];
    if (clonedMesh instanceof Mesh) {
      this.level.player?.registerCollisionMesh(clonedMesh);
    }
    for (const child of childMeshes) {
      this.level.player?.registerCollisionMesh(child);
    }

    // Create Behavior
    const behavior = createObstacleBehavior(
      spawnPoint.distribution,
      spawnPoint.position.clone(),
      spawnPoint.pathIndex,
      spawnPoint.lateralOffset
    );

    // Create Obstacle
    const obstacle = new Obstacle(
      spawnPoint.id,
      clonedMesh as Mesh, // Ensure cast or find root mesh if it's a transform node
      behavior,
      spawnPoint.distribution,
      spawnPoint.position.clone(),
      this.level,
      spawnPoint.pathIndex,
      clonedMesh instanceof Mesh ? undefined : clonedMesh // Store root if transform node
    );

    this.activeObstacles.set(spawnPoint.id, obstacle);
    spawnPoint.spawned = true;
  }

  private despawnObstacle(id: string): void {
    const obstacle = this.activeObstacles.get(id);
    if (!obstacle) return;

    obstacle.dispose();
    this.activeObstacles.delete(id);

    const spawnPoint = this.spawnPoints.find((sp) => sp.id === id);
    if (spawnPoint) {
      spawnPoint.spawned = false;
    }
  }

  /**
   * Get rotation for obstacle based on config
   */
  private getObstacleRotation(config: ObstacleDistributionConfig, pathInfo: PathInfo): Vector3 {
    let rotation = Vector3.Zero();
    if (config.rotation?.lengthSquared() > 0) {
      rotation = config.rotation.clone();
      const yRotation = Math.atan2(pathInfo.lateral.x, pathInfo.lateral.z);
      rotation.y += yRotation;
    }

    if (config.alignToPath) {
      // If obstacle comes from a pattern, add the Y rotation from lateral to the rotation
      if (config.pattern) {
        const yRotation = Math.atan2(pathInfo.lateral.x, pathInfo.lateral.z);
        rotation.y += yRotation;
      } else {
        rotation = this.level.getPathRotation(pathInfo.tangent);
      }
    }

    return rotation;
  }

  dispose(): void {
    for (const [id, obstacle] of this.activeObstacles) {
      obstacle.dispose();
    }
    this.activeObstacles.clear();
    this.spawnPoints = [];

    ObstacleSpikeRingBehavior.dispose();
    ObstacleLaserGridBehavior.dispose();
  }
}
